import Modal from '@src/components/common/Modal';
import PhotoModalTop from '@src/components/common/PhotoModalTop';
import {
  PICKER_MODAL_INSET,
  PickerModalCard,
} from '@src/components/common/pickerModalStyles';
import { useCaptionPreviewBackground } from '@src/components/sidepanel/captionPreviewStyles';
import { useKaraoke } from '@src/hooks/useKaraoke';
import { useKaraokeStore, useUserIdentity } from '@src/stores-v2/StoreContext';
import { PhotoTab } from '@src/types.ts/story';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { useMemo, useState } from 'react';
import styled from 'styled-components';

import { preparePresetsToDisplay } from '../GlobalKaraokePresetSelect';
import PresetPreview from '../PresetPreview';

import type { Preset } from '@src/components/textProcessor/TextBrandingService';
import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';

interface ReplaceCaptionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onReplace: (newTemplateId: string) => void;
  isAdding?: boolean;
  videoCreator: IVideoCreatorStore;
  activeConfigId?: string;
}

export const ReplaceCaptionsModal = ({
  isOpen,
  onClose,
  onReplace,
  isAdding = false,
  videoCreator,
  activeConfigId,
}: ReplaceCaptionsModalProps) => {
  const userIdentity = useUserIdentity();
  const { creatorStudioCaptionsEnableArborTemplates } = useFlagsCombination(
    userIdentity.currentRole,
  );
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    activeConfigId || null,
  );

  const { globalKaraokePresets } = videoCreator.textBrandingService;
  const aspectRatio = videoCreator.getCurrentAspectRatio();
  const presets = useMemo(
    preparePresetsToDisplay(globalKaraokePresets, aspectRatio),
    [globalKaraokePresets, aspectRatio],
  );

  const templates = (
    videoCreator.textBrandingService.brandingTemplates || []
  ).filter(t => {
    const templateId = typeof t.id === 'string' ? t.id : undefined;
    return !videoCreator.textBrandingService.isPresetTemplate(templateId);
  });

  const karaokeStore = useKaraokeStore();

  const { getKaraokeConfigForKaraokeClipId } = useKaraoke(
    videoCreator.rootStore.editorStore.editor,
  );

  const karaokeLine =
    videoCreator.rootStore.editorStore?.editor?.getKaraokeLines();

  const config =
    (karaokeLine?.[0]?.id
      ? getKaraokeConfigForKaraokeClipId(karaokeLine?.[0]?.id)
      : karaokeStore.defaultKaraokeConfig) ?? karaokeStore.defaultKaraokeConfig;

  const handleReplace = () => {
    if (selectedTemplateId) {
      onReplace(selectedTemplateId);
      onClose();
    }
  };

  const captionStyles = [
    ...templates,
    ...(creatorStudioCaptionsEnableArborTemplates ? presets : []),
  ];

  // Resolved from this modal's own store — the same instance the Captions row
  // in the panel uses — so the cards preview over the clip's frame rather than
  // the packaged backdrop.
  const previewBackground = useCaptionPreviewBackground(videoCreator);

  return (
    <Modal isOpen={isOpen} closeModal={onClose}>
      <PickerModalCard>
        <PhotoModalTop
          title={isAdding ? 'Add Captions' : 'Replace Captions'}
          isSelected={selectedTemplateId !== null}
          isLoading={false}
          replaceAction={handleReplace}
          tab={PhotoTab.ai}
          isAdding={isAdding}
        />

        {!captionStyles.length && (
          <EmptyState>No caption styles are available yet.</EmptyState>
        )}

        <PresetWrapper>
          <PresetsContainer>
            {captionStyles.map((t, i) => (
              <PresetPreview
                key={`${t?.id}-${t?.aspectRatio}-${i}`}
                preset={t as Preset}
                selectionOnly
                previewBackground={previewBackground}
                onSelectPreset={t => setSelectedTemplateId(t.id)}
                selected={
                  selectedTemplateId
                    ? selectedTemplateId === t?.id?.toString()
                    : !!karaokeLine?.[0]?.id &&
                      t?.id?.toString() ===
                        config?.parent_template_id?.toString()
                }
              />
            ))}
          </PresetsContainer>
        </PresetWrapper>
      </PickerModalCard>
    </Modal>
  );
};

/* Empty state per docs/design-system.md → "Empty States". */
const EmptyState = styled.div`
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 0 ${PICKER_MODAL_INSET};
  color: var(--text-muted);
  font-size: var(--text-sm);
  line-height: var(--leading-text-sm);
`;

const PresetWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  max-height: 400px;
  overflow-y: auto;
`;

/* `width: calc(100% - 40px)` on top of the 20px side padding used to inset this
   grid twice, pulling it off the title's edge. The padding alone places it. */
const PresetsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  width: 100%;
  box-sizing: border-box;
  max-height: 400px;
  overflow-y: auto;
  padding: 0 ${PICKER_MODAL_INSET};
`;
